import * as pako from 'pako';
import { CombinedError, OperationResult } from 'urql';
import { FormType, TableDialogFragment } from 'generated/graphql';

export const permittedFormMutations = [FormType.AddProductsSeller];

export interface MutationMessages {
  success: string;
  error: string;
}

export interface MutationOutcome {
  isSuccess: boolean;
  message: string;
  color?: string;
  dialogs: TableDialogFragment[];
}

// The fields the mutation payloads (editShipment, formNew) share.
interface MutationPayload {
  success?: boolean;
  hasToast?: boolean;
  toast?: { value: string; color: string } | null;
  hasDialog?: boolean;
  dialog?: TableDialogFragment | null;
  dialogs?: TableDialogFragment[] | null;
}

const SYNCED_MESSAGE = 'Your queued change was synced.';
const SYNC_FAILED_MESSAGE = 'Your queued change could not be synced.';
const syncedSummary = (total: number) => `${total} queued changes were synced.`;
const failedSummary = (failed: number, total: number, firstFailure: string) =>
  `${failed} of ${total} queued changes failed. ${firstFailure}`;

// A mutation's data holds one field, its payload: an object like editShipment or a scalar like supplyOnDate.
const payloadOf = (data: unknown) =>
  data && typeof data === 'object' ? Object.entries(data).find(([field]) => field !== '__typename')?.[1] : undefined;

export const getMutationOutcome = (
  { data, error }: Pick<OperationResult, 'data' | 'error'>,
  messages?: MutationMessages,
): MutationOutcome => {
  const successMessage = messages?.success ?? SYNCED_MESSAGE;
  const errorMessage = messages?.error ?? SYNC_FAILED_MESSAGE;
  // The auth exchange already shows the server's message for coded errors, so name the change instead.
  if (error) return { isSuccess: false, message: errorMessage, dialogs: [] };

  const payload = payloadOf(data);
  if (!payload || typeof payload !== 'object') {
    const isSuccess = payload !== false;
    return { isSuccess, message: isSuccess ? successMessage : errorMessage, dialogs: [] };
  }

  const { success, hasToast, toast, hasDialog, dialog, dialogs } = payload as MutationPayload;
  const isSuccess = success !== false;
  const serverToast = hasToast ? toast : undefined;
  return {
    isSuccess,
    message: serverToast?.value ?? (isSuccess ? successMessage : errorMessage),
    color: serverToast?.color.toLowerCase(),
    dialogs: !hasDialog ? [] : dialogs?.length ? dialogs : dialog ? [dialog] : [],
  };
};

// One outcome per drain, so a batch of queued changes gets a single summary.
export const summariseOutcomes = (outcomes: MutationOutcome[]): MutationOutcome => {
  if (outcomes.length === 1) return outcomes[0];
  const failures = outcomes.filter((outcome) => !outcome.isSuccess);
  const dialogs = outcomes.flatMap((outcome) => outcome.dialogs);
  return failures.length
    ? { isSuccess: false, message: failedSummary(failures.length, outcomes.length, failures[0].message), dialogs }
    : { isSuccess: true, message: syncedSummary(outcomes.length), dialogs };
};

// Compress data for storage
export const compressData = (data: any): Uint8Array | string => {
  const stringified = JSON.stringify(data);
  // Compress only if the stringified data is larger than 1KB to avoid overhead for small data
  return stringified.length > 1024 ? pako.deflate(stringified) : stringified;
};

// Decompress data from storage
export const restoreData = (data: Uint8Array | ArrayBuffer | string) => {
  if (!data) return null;
  try {
    // Check if data is a string (uncompressed) or Uint8Array (compressed)
    const inflatedData = typeof data === 'string' ? data : pako.inflate(data, { toText: true });

    if (!inflatedData) return null;
    return JSON.parse(inflatedData);
  } catch (error) {
    console.error('Failed to restore mutation queue:', error);
    return null;
  }
};

export const createCombinedError = (error: Error): CombinedError => ({
  name: 'CombinedError',
  message: error.message,
  graphQLErrors: [],
  networkError: error,
  response: undefined,
});
